import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaApple } from 'react-icons/fa';
import { SiAndroid } from 'react-icons/si';
import {
  Car,
  ChevronLeft,
  ChevronRight,
  IndianRupee,
  Shield,
  Sparkles,
  Users,
  Armchair,
  X,
} from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { ROUTE_PATHS } from '@/routes/routePaths';
import { BRAND } from '@/config/brandTheme';

interface AppStoreButtonsProps {
  playStoreUrl?: string;
  appStoreUrl?: string;
}

type FeatureItem = {
  icon: React.ReactNode;
  title: string;
  description: string;
  href?: string;
};

function highlightTitle(text: string) {
  const phrases = ['daily commute carpool', 'commute carpool'];
  const lower = text.toLowerCase();
  for (const phrase of phrases) {
    const idx = lower.indexOf(phrase);
    if (idx === -1) continue;
    return (
      <>
        {text.slice(0, idx)}
        <span style={{ color: BRAND.primary }}>{text.slice(idx, idx + phrase.length)}</span>
        {text.slice(idx + phrase.length)}
      </>
    );
  }
  return text;
}

function StoreButton({
  id,
  href,
  onClick,
  icon,
  iconBg,
  label,
  storeName,
}: {
  id: string;
  href: string;
  onClick: () => void;
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  storeName: string;
}) {
  return (
    <a
      id={id}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="group flex min-w-[11.5rem] items-center gap-3 rounded-xl bg-white px-4 py-3 shadow-xl transition-transform hover:-translate-y-0.5 hover:shadow-2xl"
    >
      <span
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white shadow-sm"
        style={{ background: iconBg }}
      >
        {icon}
      </span>
      <span className="text-left leading-tight">
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </span>
        <span className="block text-base font-bold text-gray-900">{storeName}</span>
      </span>
    </a>
  );
}

export const AppStoreButtons: React.FC<AppStoreButtonsProps> = ({
  playStoreUrl = 'https://play.google.com/store/apps/details?id=com.hst.duriapp',
  appStoreUrl = 'https://apps.apple.com/in/app/duri/id6755706995',
}) => {
  const { t } = useLanguage();
  const tAppStore = (key: string) => t(key, 'appStore');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [storeClicked, setStoreClicked] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const featureBar: FeatureItem[] = useMemo(
    () => [
      {
        icon: <Shield className="h-5 w-5" />,
        title: 'Safe & Secure',
        description: 'Verified guests and hosts',
      },
      {
        icon: <IndianRupee className="h-5 w-5" />,
        title: tAppStore('sosSupportTitle'),
        description: 'Split fares, save more',
      },
      {
        icon: <Armchair className="h-5 w-5" />,
        title: tAppStore('perSeatPriceTitle'),
        description: 'Pay only for your seat',
      },
      {
        icon: <Car className="h-5 w-5" />,
        title: tAppStore('privatevehicle'),
        description: 'Travel in private cars',
      },
      {
        icon: <Users className="h-5 w-5" />,
        title: tAppStore('dailyCommuteCarpoolTitle'),
        description: 'Share weekday office rides',
        href: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tAppStore closes over `t`
    [t]
  );

  const totalSlides = 3;

  useEffect(() => {
    videoRef.current?.play().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    if (storeClicked) return;

    const searchParams = new URLSearchParams(window.location.search);
    const utmCampaign = searchParams.get('utm_campaign')?.toLowerCase() || '';
    const hasDownloadAppFlag =
      utmCampaign === 'downloadapp' || searchParams.has('downloadapp');
    if (!hasDownloadAppFlag) return;

    const nav: Navigator & { vendor?: string } = navigator;
    const win: Window & { opera?: string } = window;
    const userAgent = nav.userAgent || nav.vendor || win.opera || '';

    let targetStore: 'android' | 'ios' | null = null;
    if (/android/i.test(userAgent)) targetStore = 'android';
    else if (/iphone|ipad|ipod/i.test(userAgent)) targetStore = 'ios';
    if (!targetStore) return;

    const handleRedirect = () => {
      if (storeClicked) return;
      const anchorId = targetStore === 'android' ? 'google-play-button' : 'app-store-button';
      const anchor = document.getElementById(anchorId) as HTMLAnchorElement | null;

      if (anchor) {
        if (targetStore === 'android') {
          anchor.click();
          return;
        }
        anchor.dispatchEvent(
          new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
        );
      }

      window.location.href = targetStore === 'android' ? playStoreUrl : appStoreUrl;
    };

    if (document.readyState === 'complete') {
      handleRedirect();
      return;
    }
    window.addEventListener('load', handleRedirect);
    return () => window.removeEventListener('load', handleRedirect);
  }, [playStoreUrl, appStoreUrl, storeClicked]);

  const changeSlide = (next: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(next);
    window.setTimeout(() => setIsTransitioning(false), 450);
  };

  const nextSlide = () => changeSlide((currentSlide + 1) % totalSlides);
  const prevSlide = () => changeSlide((currentSlide - 1 + totalSlides) % totalSlides);
  const goToSlide = (index: number) => {
    if (index === currentSlide) return;
    changeSlide(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) nextSlide();
    if (distance < -50) prevSlide();
  };

  return (
    <section id="app-store-buttons" className="relative">
      {/* Hero / carousel */}
      <div className="relative flex min-h-[500px] items-center justify-center overflow-hidden md:min-h-[580px] lg:min-h-[640px]">
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 h-full w-full object-cover"
            crossOrigin="anonymous"
          >
            <source
              src="https://sqlvaaziimhebqn7as.blob.core.windows.net/chaloapp/featurevideos/Duri-BackGround.mp4"
              type="video/mp4"
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/65" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#22C55E]/25 via-transparent to-[#15803D]/25" />
        </div>

        <button
          type="button"
          onClick={prevSlide}
          disabled={isTransitioning}
          className="absolute left-2 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur-md transition hover:bg-white/25 disabled:opacity-50 md:left-4 md:h-12 md:w-12 lg:left-8"
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          type="button"
          onClick={nextSlide}
          disabled={isTransitioning}
          className="absolute right-2 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur-md transition hover:bg-white/25 disabled:opacity-50 md:right-4 md:h-12 md:w-12 lg:right-8"
          aria-label="Next slide"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        <div
          className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="flex min-h-[380px] flex-col items-center justify-center gap-7 md:min-h-[440px] md:gap-8">
            <div
              className={`w-full text-center transition-all duration-450 ${
                isTransitioning ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
              }`}
            >
              {currentSlide === 0 && (
                <div className="space-y-4 md:space-y-5">
                  <h2 className="px-2 text-3xl font-bold leading-tight text-white drop-shadow-xl md:text-4xl lg:text-5xl xl:text-[3.25rem]">
                    {highlightTitle(
                      tAppStore('thanksForJoining') ||
                        'Thank you for joining our commute carpool and ride-sharing network.!'
                    )}
                  </h2>
                  <p className="px-2 text-lg font-medium text-white/95 md:text-xl">
                    {tAppStore('communityMessage1')}
                  </p>
                  <p className="px-2 text-base text-white/85 md:text-lg">
                    {tAppStore('communityMessage2')}
                  </p>
                </div>
              )}

              {currentSlide === 1 && (
                <div className="space-y-4">
                  <h2 className="px-2 text-3xl font-bold text-white drop-shadow-xl md:text-4xl lg:text-5xl">
                    {tAppStore('featuresTitle')}
                  </h2>
                  <p className="mx-auto max-w-2xl px-2 text-base text-white/90 md:text-lg">
                    {tAppStore('featuresSubtitle')}
                  </p>
                  
                </div>
              )}

              {currentSlide === 2 && (
                <div className="space-y-5">
                  <h2 className="text-3xl font-extrabold text-white drop-shadow-xl md:text-5xl">
                    Share to Save Reward Program
                  </h2>
                  <p className="text-xl font-semibold text-white/90 md:text-2xl">
                    Earn Multiple Rewards!
                  </p>
                  <p className="mx-auto max-w-3xl px-4 text-lg text-white/85 md:text-xl">
                    Get ₹125 as your First Ride Reward and ₹100 for every Refer a Friend Reward.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    className="mx-auto mt-2 flex items-center gap-2 rounded-2xl bg-brand px-8 py-3.5 font-bold text-white shadow-xl transition hover:bg-brand-dark"
                  >
                    <Sparkles className="h-5 w-5" />
                    How Duri Works
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row md:gap-5">
              <StoreButton
                id="google-play-button"
                href={playStoreUrl}
                onClick={() => setStoreClicked(true)}
                icon={<SiAndroid className="text-xl" />}
                iconBg="linear-gradient(135deg, #3DDC84, #2AB665)"
                label={tAppStore('getItOn') || 'DOWNLOAD IN'}
                storeName={tAppStore('googlePlay') || 'Google Play'}
              />
              <StoreButton
                id="app-store-button"
                href={appStoreUrl}
                onClick={() => setStoreClicked(true)}
                icon={<FaApple className="text-xl" />}
                iconBg="linear-gradient(135deg, #374151, #111827)"
                label={tAppStore('downloadOnThe') || 'DOWNLOAD IN'}
                storeName={tAppStore('appStore') || 'App Store'}
              />
            </div>

            <div className="flex items-center justify-center gap-2.5">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => goToSlide(index)}
                  disabled={isTransitioning}
                  className={`rounded-full transition-all ${
                    index === currentSlide
                      ? 'h-2.5 w-9 bg-brand shadow-md'
                      : 'h-2.5 w-2.5 bg-white/45 hover:bg-white/70'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Feature strip under hero (matches design) */}
      <div className="border-b border-gray-100 bg-white">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-6 sm:grid-cols-2 sm:px-6 md:grid-cols-3 lg:grid-cols-5 lg:gap-5 lg:px-8 lg:py-7">
          {featureBar.map((feature) => {
            const inner = (
              <>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand">
                  {feature.icon}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-gray-900">{feature.title}</span>
                  <span className="mt-0.5 line-clamp-2 block text-xs leading-snug text-gray-500">
                    {feature.description}
                  </span>
                </span>
              </>
            );

            if (feature.href) {
              return (
                <Link
                  key={feature.title}
                  to={feature.href}
                  className="flex items-start gap-3 rounded-xl p-1 transition-colors hover:bg-brand-soft/60"
                >
                  {inner}
                </Link>
              );
            }

            return (
              <div key={feature.title} className="flex items-start gap-3 p-1">
                {inner}
              </div>
            );
          })}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <div className="relative max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-white/20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 shadow-2xl">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-sm transition hover:bg-white/20"
            >
              <X className="h-6 w-6 text-white" />
            </button>
            <div className="flex items-center justify-center p-4 md:p-8">
              <img
                src="/rewardprogram.png"
                alt="Duri Reward Program Information"
                className="h-auto max-h-[70vh] w-full rounded-2xl object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
